/**
 * Validate CPF (Brazilian individual taxpayer registry)
 * @param cpf - CPF string with or without formatting
 * @returns True if valid, false otherwise
 */
export const isValidCPF = (cpf: string): boolean => {
  // Remove non-digit characters
  const cleaned = cpf.replace(/\D/g, '');

  // Check length
  if (cleaned.length !== 11) return false;

  // Check for known invalid CPFs (all digits the same)
  if (/^(\d)\1+$/.test(cleaned)) return false;

  // Validate first check digit
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleaned.charAt(i)) * (10 - i);
  }
  let checkDigit = 11 - (sum % 11);
  if (checkDigit >= 10) checkDigit = 0;
  if (checkDigit !== parseInt(cleaned.charAt(9))) return false;

  // Validate second check digit
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleaned.charAt(i)) * (11 - i);
  }
  checkDigit = 11 - (sum % 11);
  if (checkDigit >= 10) checkDigit = 0;
  if (checkDigit !== parseInt(cleaned.charAt(10))) return false;

  return true;
};

/**
 * Validate CNPJ (Brazilian company taxpayer registry)
 * @param cnpj - CNPJ string with or without formatting
 * @returns True if valid, false otherwise
 */
export const isValidCNPJ = (cnpj: string): boolean => {
  // Remove non-digit characters
  const cleaned = cnpj.replace(/\D/g, '');

  // Check length
  if (cleaned.length !== 14) return false;

  // Check for known invalid CNPJs
  if (/^(\d)\1+$/.test(cleaned)) return false;

  // Validate first check digit
  let length = cleaned.length - 2;
  let numbers = cleaned.substring(0, length);
  const digits = cleaned.substring(length);
  let sum = 0;
  let pos = length - 7;

  for (let i = length; i >= 1; i--) {
    sum += parseInt(numbers.charAt(length - i)) * pos--;
    if (pos < 2) pos = 9;
  }

  let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(0))) return false;

  // Validate second check digit
  length = length + 1;
  numbers = cleaned.substring(0, length);
  sum = 0;
  pos = length - 7;

  for (let i = length; i >= 1; i--) {
    sum += parseInt(numbers.charAt(length - i)) * pos--;
    if (pos < 2) pos = 9;
  }

  result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
  if (result !== parseInt(digits.charAt(1))) return false;

  return true;
};

/**
 * Validate email format
 * @param email - Email string
 * @returns True if valid, false otherwise
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validate Brazilian phone number
 * @param phone - Phone string with or without formatting
 * @returns True if valid, false otherwise
 */
export const isValidPhone = (phone: string): boolean => {
  const cleaned = phone.replace(/\D/g, '');
  // Check for valid lengths: 10 (landline) or 11 (mobile with 9)
  return cleaned.length === 10 || cleaned.length === 11;
};

/**
 * Validate PIX key based on type
 * @param key - PIX key string
 * @param type - Type of PIX key
 * @returns True if valid, false otherwise
 */
export const isValidPixKey = (
  key: string,
  type: 'CPF' | 'CNPJ' | 'EMAIL' | 'PHONE' | 'RANDOM'
): boolean => {
  switch (type) {
    case 'CPF':
      return isValidCPF(key);
    case 'CNPJ':
      return isValidCNPJ(key);
    case 'EMAIL':
      return isValidEmail(key);
    case 'PHONE':
      return isValidPhone(key);
    case 'RANDOM':
      // UUID v4 format
      return /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
        key
      );
    default:
      return false;
  }
};
